import React, { useEffect, useRef, useState } from "react";
import { apiV2 } from "../../../../api/axiosConfig";
import { useAuth } from "../../../../context/user";
import { useMapSettings } from "../../context/useMapSettings";
import Modal from "../../../../components/portal/Modal";
import Message from "../Message";

type SearchUser = {
    _id: string;
    clientID: string;
    clientName: string;
    email: string;
    firstName: string;
    lastName: string;
};

type SearchDevice = {
    id: string;
    name: string;
    clientName?: string;
    deviceSourceAPI: string;
};

type MessageMode = "user" | "device";

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function MessageMenu({ open, onOpenChange }: Props) {
    const { isSuperAdmin } = useAuth();
    const { settings } = useMapSettings();
    const { clientID } = settings;

    const [mode, setMode] = useState<MessageMode>("user");
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
    const [searching, setSearching] = useState(false);
    const [deviceSearch, setDeviceSearch] = useState("");
    const [deviceResults, setDeviceResults] = useState<SearchDevice[]>([]);
    const [deviceSearching, setDeviceSearching] = useState(false);
    const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<SearchDevice | null>(null);
    const [messageOpen, setMessageOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close on outside click (but not while message modal is open)
    useEffect(() => {
        if (!open || messageOpen) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onOpenChange(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open, messageOpen, onOpenChange]);

    // Clear state when closed
    useEffect(() => {
        if (!open) {
            setSearch(""); setSearchResults([]);
            setDeviceSearch(""); setDeviceResults([]);
            setSelectedUser(null); setSelectedDevice(null);
        }
    }, [open]);

    // Debounced user search
    useEffect(() => {
        const q = search.trim();
        if (!q) { setSearchResults([]); return; }
        setSearching(true);
        const params: Record<string, string> = { q };
        if (isSuperAdmin && clientID) params.clientID = clientID;
        const t = setTimeout(() => {
            apiV2.get<SearchUser[]>("ops/message-user-search", { params })
                .then((r) => setSearchResults(r.data ?? []))
                .catch(() => setSearchResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => { clearTimeout(t); setSearching(false); };
    }, [search, clientID, isSuperAdmin]); // eslint-disable-line

    // Debounced device search
    useEffect(() => {
        const q = deviceSearch.trim();
        if (!q) { setDeviceResults([]); return; }
        setDeviceSearching(true);
        const params: Record<string, string> = { q };
        if (isSuperAdmin && clientID) params.clientID = clientID;
        const t = setTimeout(() => {
            apiV2.get<SearchDevice[]>("ops/message-device-search", { params })
                .then((r) => setDeviceResults(r.data ?? []))
                .catch(() => setDeviceResults([]))
                .finally(() => setDeviceSearching(false));
        }, 300);
        return () => { clearTimeout(t); setDeviceSearching(false); };
    }, [deviceSearch, clientID, isSuperAdmin]); // eslint-disable-line

    return (
        <div ref={containerRef} className="relative">
            <button
                onClick={() => onOpenChange(!open)}
                title="Send message"
                className="flex items-center gap-1.5 px-3 h-12 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.18)] text-sm font-medium transition-colors border-none cursor-pointer bg-white text-grey hover:bg-ice"
            >
                <span className="material-icons text-[18px] leading-none">chat</span>
                <span>Message</span>
            </button>

            {open && (
                <div className="absolute top-full mt-1 left-0 w-80 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.18)] overflow-hidden z-50">
                    <div className="p-4 flex flex-col gap-3">
                        {/* Mode toggle */}
                        <div className="flex rounded-lg overflow-hidden border border-light-light-grey">
                            <button
                                onClick={() => setMode("user")}
                                className={`flex-1 text-[12px] font-medium py-1.5 border-none cursor-pointer transition-colors ${
                                    mode === "user" ? "bg-global-purple text-white" : "bg-white text-grey hover:bg-ice"
                                }`}
                            >
                                User
                            </button>
                            <button
                                onClick={() => setMode("device")}
                                className={`flex-1 text-[12px] font-medium py-1.5 border-none cursor-pointer transition-colors ${
                                    mode === "device" ? "bg-global-purple text-white" : "bg-white text-grey hover:bg-ice"
                                }`}
                            >
                                Device
                            </button>
                        </div>

                        {mode === "user" ? (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-0 mt-0">Select user to message</p>
                                <input
                                    type="text"
                                    placeholder="Search by name or email…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    autoComplete="off"
                                    className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none text-dark-dark-grey box-border"
                                />

                                {search.trim() === "" ? (
                                    <p className="text-[13px] text-light-grey m-0">Type a name or email to search</p>
                                ) : searching ? (
                                    <p className="text-[13px] text-light-grey m-0">Searching…</p>
                                ) : searchResults.length === 0 ? (
                                    <p className="text-[13px] text-light-grey m-0">No users found</p>
                                ) : (
                                    <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                                        {searchResults.map((u) => (
                                            <button
                                                key={u._id}
                                                onClick={() => { setSelectedDevice(null); setSelectedUser(u); setMessageOpen(true); }}
                                                className="flex flex-col items-start px-2 py-2 rounded-md bg-transparent border-none cursor-pointer hover:bg-ice text-left"
                                            >
                                                <span className="text-[13px] font-medium text-dark-dark-grey">
                                                    {u.firstName} {u.lastName}
                                                </span>
                                                <span className="text-[11px] text-grey">{u.email}</span>
                                                {isSuperAdmin && <span className="text-[11px] text-light-grey">{u.clientName}</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-0 mt-0">Select device to message</p>
                                <input
                                    type="text"
                                    placeholder="Search by device name…"
                                    value={deviceSearch}
                                    onChange={(e) => setDeviceSearch(e.target.value)}
                                    autoComplete="off"
                                    className="w-full text-[13px] px-2 py-1.5 border border-light-light-grey rounded-md outline-none text-dark-dark-grey box-border"
                                />

                                {deviceSearch.trim() === "" ? (
                                    <p className="text-[13px] text-light-grey m-0">Type a device name to search</p>
                                ) : deviceSearching ? (
                                    <p className="text-[13px] text-light-grey m-0">Searching…</p>
                                ) : deviceResults.length === 0 ? (
                                    <p className="text-[13px] text-light-grey m-0">No devices found</p>
                                ) : (
                                    <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                                        {deviceResults.map((d) => (
                                            <button
                                                key={d.id}
                                                onClick={() => { setSelectedUser(null); setSelectedDevice(d); setMessageOpen(true); }}
                                                className="flex flex-col items-start px-2 py-2 rounded-md bg-transparent border-none cursor-pointer hover:bg-ice text-left"
                                            >
                                                <span className="text-[13px] font-medium text-dark-dark-grey">{d.name}</span>
                                                {isSuperAdmin && d.clientName && <span className="text-[11px] text-light-grey">{d.clientName}</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {selectedUser && (
                <Modal open={messageOpen} setOpen={setMessageOpen}>
                    <Message
                        userID={selectedUser._id}
                        source="app"
                        userName={`${selectedUser.firstName} ${selectedUser.lastName}`.trim()}
                        setSendNotificationPopup={setMessageOpen}
                    />
                </Modal>
            )}

            {selectedDevice && (
                <Modal open={messageOpen} setOpen={setMessageOpen}>
                    <Message
                        deviceOID={selectedDevice.id}
                        source="satellite"
                        deviceName={selectedDevice.name}
                        deviceSourceAPI={selectedDevice.deviceSourceAPI}
                        setSendNotificationPopup={setMessageOpen}
                    />
                </Modal>
            )}
        </div>
    );
}
